import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["report", "onReportResolve", "isMobile"];
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMultiConfigReport } from 'reporting-data/tsTypes/reportTypes';
// @ts-expect-error Untyped dependency
import { getPageCount } from '../../utils/paginate';
// @ts-expect-error Untyped dependency
import ReportPaginator from './ReportPaginator';
import ReportWithRefresh from './ReportWithRefresh';
// @ts-expect-error Untyped dependency
import { usePaginationTracking } from '../../../monitoring/PaginationMetricsHook';
import { getPaginationClientLimit } from 'reporting-data/lib/paginate';
import { setLimit as setMultiConfigLimit, setOffset as setMultiConfigOffset } from 'reporting-data/report/multiConfigReportSetters';
import { jsx as _jsx } from "react/jsx-runtime";
const DEFAULT_ROW_COUNT = 5;
const getRowsPerPageChangeHandler = report => report.getIn(['displayParams', 'rowsPerPage', 'onChange']);
const getReportOffset = report => report ? report.getIn(['config', 'offset']) || 0 : 0;
const ReportWithPagination = _ref => {
  let {
      report,
      onReportResolve,
      isMobile
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const reportRef = useRef(undefined);
  const [offset, setOffset] = useState(() => getReportOffset(report));
  const [size, setSize] = useState();
  const maxPageNumber = useRef(1);
  const rowLimit = useRef(report.getIn(['config', 'limit']));
  useEffect(() => {
    const prevReport = reportRef.current;
    reportRef.current = report;
    const nextConfig = report.get('config');
    const prevConfig = prevReport && prevReport.get('config');

    // if the configs are different, reset to the new report
    if (!prevConfig || !prevConfig.equals(nextConfig) || prevReport.get('chartType') !== report.get('chartType')) {
      setOffset(getReportOffset(report));
      // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 0.
      setSize();
    }
  }, [report]);

  //This contains a hook used for using metrics to track pagination use.
  usePaginationTracking(report, maxPageNumber, rowLimit);
  const paginationRenderer = useMemo(() => {
    const limit = getPaginationClientLimit(report) || DEFAULT_ROW_COUNT;
    if (limit !== rowLimit.current) {
      rowLimit.current = limit;
    }
    if (report.get('chartType') !== 'TABLE' || size === undefined) {
      return undefined;
    }
    const page = Math.floor(offset / limit) + 1;
    const pageCount = getPageCount(size, limit);
    const handleRowsPerPageChange = getRowsPerPageChangeHandler(report);
    const handlePageChange = nextPage => {
      const nextOffset = limit * (nextPage - 1);
      setOffset(nextOffset);

      // Track the highest page number navigated to
      if (nextPage > maxPageNumber.current) {
        maxPageNumber.current = nextPage;
      }
    };
    return () => /*#__PURE__*/_jsx(ReportPaginator, {
      page: page,
      pageCount: pageCount,
      itemsPerPage: limit,
      onPageChange: handlePageChange,
      onPerPageChange: handleRowsPerPageChange,
      isMobile: isMobile
    });
  }, [report, offset, size, isMobile]);
  const handleReportResolve = useCallback(resolveInfo => {
    const {
      data
    } = resolveInfo;
    const nextSize = data.getIn(['primary', 'data', 'paginationSize']);
    if (nextSize) {
      setSize(nextSize);
    }
    if (onReportResolve) {
      onReportResolve(resolveInfo);
    }
  }, [onReportResolve]);
  const paginatedReport = useMemo(() => {
    const limit = getPaginationClientLimit(report);
    if (isMultiConfigReport(report)) {
      const configWithLimit = setMultiConfigLimit(report, limit);
      return setMultiConfigOffset(configWithLimit, offset);
    }
    return report.setIn(['config', 'offset'], offset).setIn(['config', 'limit'], limit);
  }, [report, offset]);
  return /*#__PURE__*/_jsx(ReportWithRefresh, Object.assign({
    report: paginatedReport,
    paginationRenderer: paginationRenderer,
    onReportResolve: handleReportResolve,
    isMobile: isMobile
  }, rest));
};
ReportWithPagination.propTypes = {
  report: PropTypes.object,
  runtimeOptions: PropTypes.object,
  disableEmptyStates: PropTypes.bool,
  errorRenderer: PropTypes.func,
  noDataRenderer: PropTypes.func,
  onReportResolve: PropTypes.func,
  onError: PropTypes.func,
  setIsRendered: PropTypes.func,
  resolve: PropTypes.func,
  determineVisualization: PropTypes.func,
  isMobile: PropTypes.bool,
  compact: PropTypes.bool,
  /* For debugging v2 visualizations */
  visualizationDebug: PropTypes.object
};
export default ReportWithPagination;